/**
 * Lightweight i18n plugin for BizOps.
 * Loads locale JSON files lazily (one webpack chunk per language).
 * Provides Vue.prototype.$t(key) with automatic English fallback.
 * Provides Vue.prototype.$loadLocale(lang) — returns a Promise.
 * Both English and the initial app language are preloaded in main.js
 * before the Vue instance mounts.
 */

const localeLoaders = {
  en: () => import('../locales/en.json'),
  hi: () => import('../locales/hi.json'),
  hinglish: () => import('../locales/hinglish.json')
}

const localeCache = {}
const pendingLoads = {}

function loadLocale (lang) {
  if (localeCache[lang]) return Promise.resolve(localeCache[lang])
  if (pendingLoads[lang]) return pendingLoads[lang]
  if (!localeLoaders[lang]) {
    console.warn('[i18n] No loader for language:', lang)
    return Promise.resolve({})
  }
  pendingLoads[lang] = localeLoaders[lang]()
    .then(function (module) {
      localeCache[lang] = module.default || module
      delete pendingLoads[lang]
      return localeCache[lang]
    })
    .catch(function (err) {
      console.error('[i18n] Failed to load locale:', lang, err)
      delete pendingLoads[lang]
      return {}
    })
  return pendingLoads[lang]
}

function getByPath (obj, path) {
  return path.split('.').reduce(function (curr, key) {
    return curr !== null && curr !== undefined ? curr[key] : undefined
  }, obj)
}

const i18nPlugin = {
  install: function (Vue) {
    Vue.prototype.$loadLocale = loadLocale

    Vue.prototype.$t = function (key, params) {
      var lang = (this.$root && this.$root.selectedLanguage) || 'en'
      var messages = localeCache[lang]
      var val
      if (messages) {
        val = getByPath(messages, key)
      }
      if (val === undefined || val === null) {
        var enMessages = localeCache['en'] || {}
        val = getByPath(enMessages, key)
      }
      if (val === undefined || val === null) val = key
      if (params && typeof val === 'string') {
        return val.replace(/\{(\w+)\}/g, function (_, k) {
          return params[k] != null ? params[k] : ''
        })
      }
      return val
    }

    /**
     * Translation lookup for share / outgoing messages. Honours
     * $root.shareInSelectedLanguage — when off, force English regardless of
     * the user's current UI language.
     */
    Vue.prototype.$tShare = function (key, params) {
      if (this.$root && this.$root.shareInSelectedLanguage) {
        return this.$t(key, params)
      }
      var enMessages = localeCache['en'] || {}
      var val = getByPath(enMessages, key)
      if (val === undefined || val === null) val = key
      if (params && typeof val === 'string') {
        return val.replace(/\{(\w+)\}/g, function (_, k) {
          return params[k] != null ? params[k] : ''
        })
      }
      return val
    }
  }
}

export { loadLocale, localeCache }
export default i18nPlugin



// WEBPACK FOOTER //
// ./src/plugins/i18n.js